/* istanbul ignore file */
import { ResourceData, ResourceId, useCrudService, useTimelineService } from 'framework/crud';
import { useKernelApi } from 'infrastructure/api';
import { TimelineItem, TimelineResponseData } from 'framework/ui/timeline';
import { normalizeStoragePathWithSlash } from 'domain/shared/documentStorage';
import useOpportunityDocumentService from 'domain/shared/opportunity/opportunityDocumentService';
import {
  useContractRequestService,
  usePresaleOpportunityService,
  usePresaleOutputService,
} from 'domain/shared/presale';
import opportunityFilterValidation from './list/filter/opportunityFilterValidation';
import opportunityValidation from './createupdate/opportunityValidation';
import { STRATEGY_NOTE_CREATED } from 'domain/sales/shared/timeline/strategyNote/crmStrategyNoteService';
import { isStrategyTimelineItem, StrategyTimelineItem } from 'domain/sales/shared/timeline/strategyNote/types';
import { CREATE_OPPORTUNITY } from 'domain/sales/salesService';

/**
 * Expected days for close: NEW - 1, PRESALE - 14, PROPOSAL - 10, CONTRACT - 7, SIGNING - 2, STARTING - 1.
 * @type {number}
 */
export const DAYS_TO_CLOSE = 35;
export const POSTPONED = 'POSTPONED';
export const NEW = 'NEW';
export const CLOSED_WON = 'CLOSED_WON';
export const CLOSED_LOST = 'CLOSED_LOST';
export const CONTRACT = 'CONTRACT';
export const SIGNING = 'SIGNING';

export const OPPORTUNITY_STAGES_ALIASES = {
  NEW,
  PRESALE: 'PRESALE',
  PROPOSAL: 'PROPOSAL',
  CONTRACT,
  SIGNING,
  STARTING: 'STARTING',
  CLOSED_LOST,
  CLOSED_WON,
  POSTPONED,
};

export const OPPORTUNITY_STAGES = {
  [NEW]: 'New',
  PRESALE: 'Presale',
  PROPOSAL: 'Proposal',
  CONTRACT: 'Contract',
  SIGNING: 'Signing',
  STARTING: 'Starting',
  [CLOSED_LOST]: 'Closed Lost',
  [CLOSED_WON]: 'Closed Won',
  [POSTPONED]: 'Postponed',
};

const OPPORTUNITY_STAGE_STATUSES = {
  IN_PROGRESS: 'IN_PROGRESS',
  IN_PROGRESS_WITH_DELAY: 'IN_PROGRESS_WITH_DELAY',
  COMPLETED: 'COMPLETED',
  COMPLETED_WITH_DELAY: 'COMPLETED_WITH_DELAY',
  NOT_STARTED: 'NOT_STARTED',
  FAIL: 'FAIL',
};

export const OPPORTUNITY_STAGES_ALLOWED_TRANSITIONS = {
  [OPPORTUNITY_STAGES_ALIASES.NEW]: [OPPORTUNITY_STAGES_ALIASES.PRESALE, OPPORTUNITY_STAGES_ALIASES.CLOSED_LOST],
  [OPPORTUNITY_STAGES_ALIASES.PRESALE]: [
    OPPORTUNITY_STAGES_ALIASES.PROPOSAL,
    OPPORTUNITY_STAGES_ALIASES.POSTPONED,
    OPPORTUNITY_STAGES_ALIASES.CLOSED_LOST,
  ],
  [OPPORTUNITY_STAGES_ALIASES.PROPOSAL]: [
    OPPORTUNITY_STAGES_ALIASES.CONTRACT,
    OPPORTUNITY_STAGES_ALIASES.POSTPONED,
    OPPORTUNITY_STAGES_ALIASES.CLOSED_LOST,
  ],
  [OPPORTUNITY_STAGES_ALIASES.CONTRACT]: [
    OPPORTUNITY_STAGES_ALIASES.SIGNING,
    OPPORTUNITY_STAGES_ALIASES.POSTPONED,
    OPPORTUNITY_STAGES_ALIASES.CLOSED_LOST,
  ],
  [OPPORTUNITY_STAGES_ALIASES.SIGNING]: [
    OPPORTUNITY_STAGES_ALIASES.STARTING,
    OPPORTUNITY_STAGES_ALIASES.POSTPONED,
    OPPORTUNITY_STAGES_ALIASES.CLOSED_LOST,
  ],
  [OPPORTUNITY_STAGES_ALIASES.STARTING]: [
    OPPORTUNITY_STAGES_ALIASES.CLOSED_WON,
    OPPORTUNITY_STAGES_ALIASES.POSTPONED,
    OPPORTUNITY_STAGES_ALIASES.CLOSED_LOST,
  ],
};

export const isCurrentOpportunityStage = (stateStatus: string) =>
  [OPPORTUNITY_STAGE_STATUSES.IN_PROGRESS, OPPORTUNITY_STAGE_STATUSES.IN_PROGRESS_WITH_DELAY].includes(stateStatus);

export const isCompletedOpportunityStage = (stateStatus: string) =>
  [OPPORTUNITY_STAGE_STATUSES.COMPLETED, OPPORTUNITY_STAGE_STATUSES.COMPLETED_WITH_DELAY].includes(stateStatus);

export const isFailedOpportunityStage = (stateStatus: string) =>
  [OPPORTUNITY_STAGE_STATUSES.FAIL].includes(stateStatus);

export const PROBABILITY_MAP: Record<string, number> = {
  NEW: 10,
  PRESALE: 20,
  PROPOSAL: 30,
  CONTRACT: 80,
  SIGNING: 90,
  STARTING: 95,
  CLOSED_LOST: 0,
  CLOSED_WON: 100,
  [POSTPONED]: 20,
};

export const OPPORTUNITY_TYPE = {
  EXISTING_BUSINESS: 'Existing business',
  NEW_BUSINESS: 'New business',
};

const REASON = {
  UNRESPONSIVE_CONTACT: 'Contact became unresponsive',
  CANCELLED_OR_POSTPONED: 'Project cancelled or postponed',
  IN_HOUSE_IMPLEMENTATION: 'Decided to implement the project in-house',
  ANOTHER_VENDOR: 'Another vendor selected',
  ALREADY_WORKING_WITH: 'We are an incumbent supplier (Customer is already working with us)',
  PROPOSAL_QUALITY: 'Proposal quality',
  PRICE: 'Price of the service',
  DELIVERY_SPEED: 'Timeline (speed of delivery)',
  EXPERTISE: 'Expertise and/or qualifications',
  PROVIDED_REFERENCES: 'Provided References',
  GEOGRAPHY: 'Geography of our offices (provide details in the Summary)',
  SALESPERSON: 'Salesperson responsiveness and/or guidance',
  PROJECT_STARTED: 'Project has been started via process',
  OTHER: 'Other (provide details in the Summary)',
};
export const CLOSED_REASON_LIST = REASON;

export const CLOSED_REASON = {
  PRIMARY: {
    CLOSED_WON: {
      ALREADY_WORKING_WITH: REASON.ALREADY_WORKING_WITH,
      PROPOSAL_QUALITY: REASON.PROPOSAL_QUALITY,
      PRICE: REASON.PRICE,
      DELIVERY_SPEED: REASON.DELIVERY_SPEED,
      EXPERTISE: REASON.EXPERTISE,
      PROVIDED_REFERENCES: REASON.PROVIDED_REFERENCES,
      GEOGRAPHY: REASON.GEOGRAPHY,
      SALESPERSON: REASON.SALESPERSON,
      PROJECT_STARTED: REASON.PROJECT_STARTED,
      OTHER: REASON.OTHER,
    },
    CLOSED_LOST: {
      UNRESPONSIVE_CONTACT: REASON.UNRESPONSIVE_CONTACT,
      CANCELLED_OR_POSTPONED: REASON.CANCELLED_OR_POSTPONED,
      IN_HOUSE_IMPLEMENTATION: REASON.IN_HOUSE_IMPLEMENTATION,
      ANOTHER_VENDOR: REASON.ANOTHER_VENDOR,
      PROPOSAL_QUALITY: REASON.PROPOSAL_QUALITY,
      PRICE: REASON.PRICE,
      DELIVERY_SPEED: REASON.DELIVERY_SPEED,
      EXPERTISE: REASON.EXPERTISE,
      PROVIDED_REFERENCES: REASON.PROVIDED_REFERENCES,
      GEOGRAPHY: REASON.GEOGRAPHY,
      SALESPERSON: REASON.SALESPERSON,
      OTHER: REASON.OTHER,
    },
  },

  SECONDARY: {
    CLOSED_WON: {
      PROPOSAL_QUALITY: REASON.PROPOSAL_QUALITY,
      PRICE: REASON.PRICE,
      DELIVERY_SPEED: REASON.DELIVERY_SPEED,
      EXPERTISE: REASON.EXPERTISE,
      PROVIDED_REFERENCES: REASON.PROVIDED_REFERENCES,
      GEOGRAPHY: REASON.GEOGRAPHY,
      SALESPERSON: REASON.SALESPERSON,
      OTHER: REASON.OTHER,
    },
    get CLOSED_LOST() {
      return this.CLOSED_WON;
    },
  },
};

export const READ_FULL_OPPORTUNITIES_LIST = 'READ_FULL_OPPORTUNITIES_LIST';
export const READ_OPPORTUNITY = 'READ';
export const UPDATE_OPPORTUNITY = 'UPDATE';
export const UPDATE_OPPORTUNITY_FROM_LIST_PAGE = CREATE_OPPORTUNITY;
export const DELETE_OPPORTUNITY = 'DELETE';
export const CREATE_PRESALE_REQUEST = 'CREATE_PRESALE_REQUEST';
export const UPDATE_PRESALE_REQUEST = 'UPDATE_PRESALE_REQUEST';
export const CREATE_PRESALE_OUTPUT = 'CREATE_PRESALE_OUTPUT';
export const UPDATE_PRESALE_OUTPUT = 'UPDATE_PRESALE_OUTPUT';
export const CREATE_CONTRACT_REQUEST = 'CREATE_CONTRACT_REQUEST';
export const UPDATE_CONTRACT_REQUEST = 'UPDATE_CONTRACT_REQUEST';
export const PRESALE_ITEMS_TYPE = {
  PRESALE_REQUEST: 'presale-requests',
  PRESALE_OUTPUT: 'presale-outputs',
  CONTRACT_REQUEST: 'contract-requests',
};

const useOpportunityService = () => {
  const { sendPatchRequest } = useKernelApi();
  const { commitDocuments, searchDocuments, getDocuments, generatePresignedLink, downloadFileContent } =
    useOpportunityDocumentService();

  const baseCrudRequests = useCrudService({
    singleResourceUrl: '/crm/opportunities/:id',
    listResourceUrl: '/crm/opportunities',
    useService: useKernelApi,
  });

  const { createPresaleRequest } = usePresaleOpportunityService();
  const { createOutput } = usePresaleOutputService();
  const { create } = useContractRequestService();

  const commitArtifactPaths = async (
    opportunityId: StringOrNumber,
    artifactPaths: string[] = [],
    documentType: string,
  ) => {
    const opportunityScopePrefix = `/opportunity/${opportunityId}/`;
    const normalizedArtifactPaths = artifactPaths.map(path => normalizeStoragePathWithSlash(path));
    const permanentPaths = normalizedArtifactPaths.filter(path => path?.startsWith(opportunityScopePrefix));
    const tempPaths = normalizedArtifactPaths.filter(path => path && !path.startsWith(opportunityScopePrefix));

    if (!tempPaths.length) {
      return [...new Set(permanentPaths)];
    }
    const response = await commitDocuments(opportunityId, {
      paths: tempPaths,
      metadata: {
        documentType,
        source: 'crm-ui',
      },
    });
    const committedPaths = Array.isArray(response?.paths) ? response.paths : response?.path ? [response.path] : [];

    return [...new Set([...permanentPaths, ...committedPaths])];
  };

  const getValidationSchema = () => Promise.resolve(opportunityValidation);

  const {
    search,
    create: rawCreateTimelineItem,
    getTimelinePageById,
  } = useTimelineService({
    parentEntityUrl: `/crm/opportunities/:id`,
    useService: useKernelApi,
  });

  const { search: searchAccountTimeline } = useTimelineService({
    parentEntityUrl: `/crm/accounts/:id`,
    useService: useKernelApi,
  });

  type OpportunityAccountLink = {
    accountId?: StringOrNumber;
  };

  const getPinnedStrategyNotesFromAccount = async (
    opportunityId: StringOrNumber,
    requestData: ResourceData,
  ): Promise<StrategyTimelineItem[]> => {
    const opportunity = (await baseCrudRequests.getById(opportunityId)) as OpportunityAccountLink;

    if (!opportunity.accountId) {
      return [];
    }

    const accountTimelineResponse = (await searchAccountTimeline(opportunity.accountId, {
      ...requestData,
      filter: {
        type: {
          eq: STRATEGY_NOTE_CREATED,
        },
        ...requestData?.filter,
      },
    })) as unknown as { result: TimelineItem[] };

    return accountTimelineResponse.result.filter(isStrategyTimelineItem);
  };

  const searchTimelineItems = async (opportunityId: StringOrNumber, requestData: ResourceData) => {
    const pinRespoce = await search(opportunityId, {
      ...requestData,
      filter: {
        ...requestData?.filter,
        isPinned: {
          eq: true,
        },
      },
    });

    const unPinRespoce = await search(opportunityId, {
      ...requestData,
      filter: {
        ...requestData?.filter,
        isPinned: {
          eq: false,
        },
      },
    });

    const strategyNotesFromAccount = await getPinnedStrategyNotesFromAccount(opportunityId, requestData);
    const pinnedItems = pinRespoce.result as unknown as TimelineItem[];
    const unpinnedItems = unPinRespoce.result as unknown as TimelineItem[];
    const opportunityTimelineItems = [...pinnedItems, ...unpinnedItems].filter(
      (item: TimelineItem) => !isStrategyTimelineItem(item),
    );
    const timelineItemsById = new Map<string, TimelineItem>();

    [...strategyNotesFromAccount, ...opportunityTimelineItems].forEach(item => {
      if (!item?.id) {
        return;
      }

      timelineItemsById.set(String(item.id), item);
    });

    return {
      ...unPinRespoce,
      result: Array.from(timelineItemsById.values()),
    } as unknown as TimelineResponseData;
  };

  const createTimelineItem = (oppId: ResourceId, itemType: string, request: ResourceData) => {
    if (itemType === PRESALE_ITEMS_TYPE.PRESALE_REQUEST) {
      return createPresaleRequest(oppId, request);
    }
    if (itemType === PRESALE_ITEMS_TYPE.PRESALE_OUTPUT) {
      return createOutput(oppId, request);
    }
    if (itemType === PRESALE_ITEMS_TYPE.CONTRACT_REQUEST) {
      return create(oppId, request);
    }
    return rawCreateTimelineItem(oppId, itemType, request);
  };

  const pinOpportunitieTimeline = async (oppId: ResourceId, timelineId: ResourceId, pinStatus: boolean) => {
    const request = await sendPatchRequest(`/crm/opportunities/${oppId}/timeline/${timelineId}`, {
      isPinned: pinStatus,
    });

    return request.json();
  };

  const getFilterValidationSchema = () => Promise.resolve(opportunityFilterValidation);

  return {
    commitDocuments,
    commitArtifactPaths,
    searchDocuments,
    getDocuments,
    generatePresignedLink,
    downloadFileContent,
    getValidationSchema,
    searchTimelineItems,
    createTimelineItem,
    getTimelinePageById,
    ...baseCrudRequests,
    getFilterValidationSchema,
    pinOpportunitieTimeline,
  };
};

export default useOpportunityService;
